import { Controller } from '@hotwired/stimulus';

const VALUE_FORMAT_CURRENCY = 'currency';
const VALUE_FORMAT_PERCENT = 'percent';
const VALUE_FORMAT_PERCENTAGE = 'percentage';

const COLUMN_TYPE_TEXT = 'text';
const COLUMN_TYPE_NUMBER = 'number';
const COLUMN_TYPE_BOOLEAN = 'boolean';

const ROW_TYPE_NORMAL = 'normal';
const ROW_TYPE_BOOLEAN = 'boolean';
const ROW_TYPE_GRAND_TOTAL_DETAIL = 'grand_total_detail';
const ROW_TYPE_SECTION_TOTAL = 'section_total';

const COLUMN_KEY_GRAND_TOTAL = 'grand_total';

export default class extends Controller {
  static targets = [
    'businessSelect',
    'formulaSelect',
    'formulaDescription',
    'loadButton',
    'loadingIndicator',
    'loadingText',
    'errorMessage',
    'errorText',
    'editorContainer',
    'tableContainer',
    'jsonOutput',
    'saveButton',
    'testResult',
    'addSectionButton',
    'hiddenField',
  ];

  static values = {
    formulaClass: String,
    businessId: String,
    reportingHistoryType: String,
    reportingHistoryId: String,
    initialData: Object,
    currency: String,
  };

  connect() {
    this.currentJson = null;
    this.currentFormulaClass = null;
    this.currentBusinessId = null;
    this.currentReportingHistoryType = null;
    this.currentReportingHistoryId = null;
    this.autoSaveTimeout = null;
    this.isAutoSaving = false;
    this.pendingChanges = false; // Track if there are unsaved changes
    this.focusedElement = null; // Track currently focused element

    // Load businesses if we have the dropdown
    if (this.hasBusinessSelectTarget) {
      this.loadBusinesses();
    }

    // If we have initial data (in modal), load it automatically
    if (this.hasInitialDataValue && this.initialDataValue) {
      try {
        // Parse JSON if it's a string, otherwise use as-is
        this.currentJson =
          typeof this.initialDataValue === 'string'
            ? JSON.parse(this.initialDataValue)
            : this.initialDataValue;

        this.currentFormulaClass = this.formulaClassValue;
        this.currentBusinessId = this.businessIdValue;
        this.currentReportingHistoryType = this.reportingHistoryTypeValue;
        this.currentReportingHistoryId = this.reportingHistoryIdValue;
        this.renderTable();
        this.updateJsonOutput();
        this.showEditor();
      } catch (error) {
        this.showError('Failed to parse formula data');
      }
    }
  }

  disconnect() {
    // Clean up timeout when controller disconnects
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
      this.autoSaveTimeout = null;
    }
  }

  // Open modal with selected business and formula
  async openModal() {
    const formulaClass = this.formulaSelectTarget.value;
    const businessId = this.hasBusinessSelectTarget ? this.businessSelectTarget.value : null;

    if (!formulaClass) {
      alert('Please select a formula');
      return;
    }

    // if (!businessId) {
    //   alert('Please select a business');
    //   return;
    // }

    // Navigate to the edit route which will open in the turbo frame modal
    const url = `/data_points/formulas/${formulaClass}/edit?business_id=${businessId}`;
    window.Turbo.visit(url, { frame: 'remote_modal' });
  }

  async loadBusinesses() {
    try {
      const response = await fetch('/data_points/formulas/businesses', {
        headers: {
          'X-CSRF-Token': this.getCsrfToken(),
        },
      });

      if (!response.ok) {
        console.warn('Could not load businesses, using default list');
        return;
      }

      const businesses = await response.json();

      // Clear existing options (except the first placeholder)
      while (this.businessSelectTarget.options.length > 1) {
        this.businessSelectTarget.remove(1);
      }

      // Add business options
      businesses.forEach((business) => {
        const option = document.createElement('option');
        option.value = business.id;
        option.textContent = business.name;
        this.businessSelectTarget.appendChild(option);
      });
    } catch (error) {
      console.error('Error loading businesses:', error);
    }
  }

  async loadFormulaWithBusiness() {
    const formulaClass = this.formulaSelectTarget.value;
    const businessId = this.hasBusinessSelectTarget ? this.businessSelectTarget.value : null;

    if (!formulaClass) {
      this.showError('Please select a formula');
      return;
    }

    this.currentFormulaClass = formulaClass;
    this.currentBusinessId = businessId;

    this.showLoading();
    this.hideError();

    try {
      const requestBody = {
        formula_class: formulaClass,
        business_id: businessId,
      };

      const response = await fetch('/data_points/formulas/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to load formula: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      this.currentJson = data;
      this.renderTable();
      this.updateJsonOutput();
      this.showEditor();
    } catch (error) {
      console.error('Error loading formula:', error);
      this.showError(error.message);
    } finally {
      this.hideLoading();
    }
  }

  async saveFormula(calculatedOnly = false) {
    if (!this.currentJson || !this.currentFormulaClass) {
      console.error('No formula loaded');
      if (!calculatedOnly) {
        this.showError('No formula loaded');
      }
      return;
    }

    // Don't show loading indicator for auto-save
    if (!calculatedOnly) {
      this.showLoading('Calculating totals...');
      this.hideError();
    }

    try {
      const requestBody = {
        formula_class: this.currentFormulaClass,
        entry_json: this.currentJson,
        business_id: this.currentBusinessId,
        reporting_history_type: this.currentReportingHistoryType,
        reporting_history_id: this.currentReportingHistoryId,
      };

      const response = await fetch('/data_points/formulas/calculate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save formula: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (calculatedOnly) {
        // Only update calculated fields (fields with calculated: true in column definition)
        this.updateCalculatedFieldsOnly(data);
      } else {
        // Full update - replace entire JSON
        this.currentJson = data;
      }

      // Re-render the table with updated values
      // For auto-save (calculatedOnly = true), preserve focus
      this.renderTable(calculatedOnly);
      this.updateJsonOutput();

      // Show success message only for manual saves
      if (!calculatedOnly) {
        this.showSuccess('Formula calculated and updated successfully!');
      }
    } catch (error) {
      if (!calculatedOnly) {
        this.showError(error.message);
      } else {
        console.error('Auto-save error:', error);
      }
    } finally {
      if (!calculatedOnly) {
        this.hideLoading();
      }
    }
  }

  updateCalculatedFieldsOnly(newData) {
    // Get list of calculated column keys
    const calculatedKeys = this.currentJson.columns
      .filter((col) => col.calculated === true)
      .map((col) => col.key);

    // Update only calculated fields in each row
    this.currentJson.sections.forEach((section, sIndex) => {
      section.groups.forEach((group, gIndex) => {
        group.rows.forEach((row, rIndex) => {
          const newRow = newData.sections[sIndex]?.groups[gIndex]?.rows[rIndex];
          if (!newRow) return;

          // Update calculated fields only
          calculatedKeys.forEach((key) => {
            if (row.values && newRow.values && newRow.values[key] !== undefined) {
              row.values[key] = newRow.values[key];
            }
          });

          // Also update calculated row types (subtotals, grand totals, etc.)
          if (row.row_type !== ROW_TYPE_NORMAL && !row.user_editable) {
            Object.keys(row.values || {}).forEach((key) => {
              if (newRow.values && newRow.values[key] !== undefined) {
                row.values[key] = newRow.values[key];
              }
            });
          }
        });
      });
    });
  }

  // Debounced auto-save that triggers after user stops editing
  scheduleAutoSave() {
    // Mark that we have pending changes
    this.pendingChanges = true;

    // Clear existing timeout
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }

    // Schedule new auto-save after 2 seconds of inactivity (increased from 500ms)
    this.autoSaveTimeout = setTimeout(() => {
      this.performAutoSave();
    }, 2000);
  }

  async performAutoSave() {
    if (this.isAutoSaving || !this.pendingChanges) return;

    this.isAutoSaving = true;
    try {
      await this.saveFormula(true);
      this.pendingChanges = false; // Clear pending changes flag
    } finally {
      this.isAutoSaving = false;
    }
  }

  renderTable(preserveFocus = false) {
    if (!this.currentJson) return;

    if (!this.validateJson()) return;

    // Save focus state before render
    if (preserveFocus) {
      this.saveFocusState();
    }

    this.resetRenderState();
    this.detectRowDimension();
    this.deriveLevelColumns();

    const table = this.buildTable();

    this.tableContainerTarget.innerHTML = '';
    this.tableContainerTarget.appendChild(table);

    this.updateAddSectionButtonVisibility();

    // Restore focus after render
    if (preserveFocus) {
      this.restoreFocusState();
    }
  }

  saveFocusState() {
    const activeElement = document.activeElement;
    if (
      (activeElement && activeElement.tagName === 'INPUT') ||
      activeElement.tagName === 'SELECT'
    ) {
      // Store the row/column position and value to identify the element later
      const cell = activeElement.closest('td');
      if (cell) {
        const row = cell.closest('tr');
        const table = row.closest('table');
        const rowIndex = Array.from(table.querySelectorAll('tr')).indexOf(row);
        const cellIndex = Array.from(row.children).indexOf(cell);

        this.focusedElement = {
          rowIndex,
          cellIndex,
          selectionStart: activeElement.selectionStart,
          selectionEnd: activeElement.selectionEnd,
        };
      }
    }
  }

  restoreFocusState() {
    if (!this.focusedElement) return;

    try {
      const table = this.tableContainerTarget.querySelector('table');
      const row = table.querySelectorAll('tr')[this.focusedElement.rowIndex];
      const cell = row?.children[this.focusedElement.cellIndex];
      const input = cell?.querySelector('input, select');

      if (input) {
        input.focus();
        if (input.tagName === 'INPUT' && this.focusedElement.selectionStart !== undefined) {
          input.setSelectionRange(
            this.focusedElement.selectionStart,
            this.focusedElement.selectionEnd
          );
        }
      }
    } catch (error) {
      console.debug('Could not restore focus:', error);
    } finally {
      this.focusedElement = null;
    }
  }

  /* ======================================================
   * VALIDATION & SETUP
   * ====================================================== */

  validateJson() {
    if (this.currentJson.error) {
      console.error('Formula generation error:', this.currentJson.error);
      this.showError(
        typeof this.currentJson.error === 'string'
          ? this.currentJson.error
          : 'Failed to generate formula'
      );
      return false;
    }

    if (!Array.isArray(this.currentJson.columns)) {
      console.error('Invalid JSON structure:', this.currentJson);
      this.showError('Invalid table structure - columns missing');
      return false;
    }

    if (!Array.isArray(this.currentJson.sections)) {
      console.error('Invalid JSON structure:', this.currentJson);
      this.showError('Invalid table structure - sections missing');
      return false;
    }

    return true;
  }

  resetRenderState() {
    this._detectedRowDimensionKey = null;
    this._levelColumnKeys = null;
  }

  /* ======================================================
   * ROW DIMENSION DETECTION
   * ====================================================== */

  detectRowDimension() {
    const { columns, sections } = this.currentJson;

    outer: for (const col of columns) {
      const key = col.key;

      for (const section of sections) {
        for (const group of section.groups) {
          for (const row of group.rows) {
            if (!this.isRenderableRow(row)) continue;

            const val = this.getCellValue(row, key);
            if (typeof val === 'string' && val !== '') {
              this._detectedRowDimensionKey = key;
              break outer;
            }
          }
        }
      }
    }
  }

  isRenderableRow(row) {
    return (
      row && (row.row_type === ROW_TYPE_NORMAL || row.row_type === ROW_TYPE_GRAND_TOTAL_DETAIL)
    );
  }

  getCellValue(row, key) {
    return row.values?.[key] ?? row[key];
  }

  /* ======================================================
   * LEVEL COLUMNS
   * ====================================================== */

  deriveLevelColumns() {
    const allKeys = this.currentJson.columns.map((c) => c.key);

    if (this._detectedRowDimensionKey) {
      const idx = allKeys.indexOf(this._detectedRowDimensionKey);
      this._levelColumnKeys = idx > 0 ? allKeys.slice(0, idx) : allKeys.slice(0, 2);
      return;
    }

    const guessed = allKeys.filter((k) => k.startsWith('level_') || k.startsWith('level'));

    this._levelColumnKeys = guessed.length ? guessed : allKeys.slice(0, 2);
  }

  /* ======================================================
   * TABLE BUILDERS
   * ====================================================== */

  buildTable() {
    const table = document.createElement('table');
    table.className = 'formula-table';

    table.appendChild(this.renderHeader());
    table.appendChild(this.renderBody());

    return table;
  }

  renderHeader() {
    const thead = document.createElement('thead');
    const tr = document.createElement('tr');

    this.currentJson.columns.forEach((col) => {
      const th = document.createElement('th');
      th.textContent = col.label || col.key;
      tr.appendChild(th);
    });

    thead.appendChild(tr);
    return thead;
  }

  renderBody() {
    const tbody = document.createElement('tbody');

    this.currentJson.sections.forEach((section, sectionIndex) => {
      if (!section) return;
      this.renderSection(tbody, section, sectionIndex);
    });

    return tbody;
  }

  /* ======================================================
   * SECTIONS & ROWS
   * ====================================================== */

  renderSection(tbody, section, sectionIndex) {
    const flatRows = this.flattenRows(section, sectionIndex);
    const mergeInfo = this.buildMergeInfo(flatRows);

    flatRows.forEach((entry, rowIdx) => {
      const tr = document.createElement('tr');

      this.currentJson.columns.forEach((col) => {
        if (this.isHierarchyColumn(col.key)) {
          this.renderHierarchyCell(tr, col.key, mergeInfo, entry, rowIdx, section);
        } else {
          this.renderDataCell(tr, col, entry);
        }
      });

      tbody.appendChild(tr);
    });
  }

  flattenRows(section, sectionIndex) {
    const rows = [];

    section.groups.forEach((group, groupIndex) => {
      group.rows.forEach((row, rowIndex) => {
        rows.push({ row, sectionIndex, groupIndex, rowIndex });
      });
    });

    return rows;
  }

  /* ======================================================
   * MERGE / ROWSPAN LOGIC
   * ====================================================== */

  buildMergeInfo(flatRows) {
    const mergeInfo = {};

    this.currentJson.columns.forEach((col) => {
      const key = col.key;
      if (!this.isHierarchyColumn(key)) return;

      mergeInfo[key] = [];
      let start = 0;

      for (let i = 1; i <= flatRows.length; i++) {
        const prev = this.getHierarchyValue(flatRows[start].row, key);
        const curr = i < flatRows.length ? this.getHierarchyValue(flatRows[i].row, key) : null;

        if (i === flatRows.length || curr !== prev) {
          mergeInfo[key].push({
            startIndex: start,
            rowSpan: i - start,
            value: prev,
          });
          start = i;
        }
      }
    });

    return mergeInfo;
  }

  /* ======================================================
   * HIERARCHY CELLS
   * ====================================================== */

  isHierarchyColumn(key) {
    return (
      key.startsWith('level_') ||
      ['gender', 'employment_type', 'contract_status'].includes(key) ||
      this.isRowDimensionKey(key)
    );
  }

  renderHierarchyCell(tr, key, mergeInfo, entry, rowIdx, section) {
    const block = mergeInfo[key]?.find((m) => m.startIndex === rowIdx);
    if (!block) return;

    const td = document.createElement('td');
    td.rowSpan = block.rowSpan;
    td.classList.add('hierarchy-cell');

    const isEditable = key === this._levelColumnKeys[0] && section.editable;

    if (isEditable) {
      td.appendChild(this.buildEditableSectionName(entry, block.value));
    } else {
      td.textContent = block.value || '';
    }

    tr.appendChild(td);
  }

  buildEditableSectionName(entry, value) {
    const container = document.createElement('div');
    container.className = 'section-name-container';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.alignItems = 'flex-start';

    const input = document.createElement('input');
    input.type = COLUMN_TYPE_TEXT;
    input.value = value || '';
    input.className = 'section-name-input';

    input.addEventListener('input', (e) => {
      const newName = e.target.value;
      const section = this.currentJson.sections[entry.sectionIndex];

      section.name = newName;

      section.groups.forEach((group) => {
        group.rows.forEach((r) => {
          if (r[this._levelColumnKeys[0]] !== undefined) {
            r[this._levelColumnKeys[0]] = newName;
          }
          if (r.values?.[this._levelColumnKeys[0]] !== undefined) {
            r.values[this._levelColumnKeys[0]] = newName;
          }
        });
      });

      this.updateJsonOutput();
    });

    // Prevent Enter key from submitting the form
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });

    container.appendChild(input);

    const section = this.currentJson.sections[entry.sectionIndex];
    if (section.is_extra) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'section-delete-btn';
      btn.innerHTML = '×';
      btn.title = 'Remove this section';
      btn.style.marginTop = '0.5rem';
      btn.style.alignSelf = 'flex-start';
      btn.addEventListener('click', () => {
        this.removeSection(entry.sectionIndex);
      });
      container.appendChild(btn);
    }

    return container;
  }

  /* ======================================================
   * DATA CELLS
   * ====================================================== */

  // Map highlight identifiers to CSS colors
  getHighlightColor(identifier) {
    const colorMap = {
      highlight_grand_total: 'var(--dark-grey-color)',
      highlight_sub_total: 'var(--grey-color)',
      highlight_average: 'var(--grey-color)',
      highlight_qualitative: 'var(--white-color)',
      highlight_separator: 'header-gradient', // Use CSS class instead of inline color
    };

    return colorMap[identifier] || identifier;
  }

  renderDataCell(tr, col, entry) {
    const td = document.createElement('td');
    const { row } = entry;
    const key = col.key;
    const value = row.values?.[key];
    const valueFormat = this.getValueFormat(entry, col);

    const styles = row.cell_styles?.[key];
    const isBlank = styles?.blank === true;
    const isDisabled = styles?.disabled === true;

    if (styles?.bg && !isBlank) {
      const bgColor = this.getHighlightColor(styles.bg);
      if (bgColor === 'header-gradient') {
        td.classList.add('header-gradient-bg');
      } else {
        td.style.backgroundColor = bgColor;
      }
    }

    if (!this.isEditableCell(row, key, styles)) {
      const formattedValue = this.formatCellValue(value, valueFormat);
      td.textContent = isBlank || isDisabled ? '' : valueFormat ? formattedValue : (value ?? '');
      tr.appendChild(td);
      return;
    }

    const input = this.buildEditableInput(col, value, entry);
    if (this.shouldShowValueFormat(col, valueFormat)) {
      const wrapper = document.createElement('div');
      wrapper.className = 'formula-input-wrapper';

      if (valueFormat === VALUE_FORMAT_CURRENCY) {
        const prefix = document.createElement('span');
        prefix.className = 'formula-input-prefix';
        prefix.textContent = this.getCurrencySymbol();
        wrapper.appendChild(prefix);
      }

      wrapper.appendChild(input);

      if (valueFormat === VALUE_FORMAT_PERCENT || valueFormat === VALUE_FORMAT_PERCENTAGE) {
        const suffix = document.createElement('span');
        suffix.className = 'formula-input-suffix';
        suffix.textContent = '%';
        wrapper.appendChild(suffix);
      }

      td.appendChild(wrapper);
    } else {
      td.appendChild(input);
    }
    tr.appendChild(td);
  }

  isEditableCell(row, key, styles) {
    const isBooleanRow = row.row_type === ROW_TYPE_BOOLEAN;
    const isUserEditable = row.user_editable === true;

    if (isUserEditable) {
      const grandTotalAllowed = row.user_editable_grand_total === true;
      return (
        (key !== COLUMN_KEY_GRAND_TOTAL || grandTotalAllowed) &&
        styles?.blank !== true &&
        styles?.disabled !== true
      );
    }

    return (
      (row.row_type === ROW_TYPE_NORMAL || isBooleanRow) &&
      key !== COLUMN_KEY_GRAND_TOTAL &&
      styles?.blank !== true &&
      styles?.disabled !== true
    );
  }

  getValueFormat(entry, col) {
    if (col.type === COLUMN_TYPE_TEXT || col.type === COLUMN_TYPE_BOOLEAN) return null;
    return entry.row?.value_format || null;
  }

  shouldShowValueFormat(col, valueFormat) {
    if (!valueFormat) return false;
    if (col.type === COLUMN_TYPE_TEXT || col.type === COLUMN_TYPE_BOOLEAN) return false;
    return (
      valueFormat === VALUE_FORMAT_CURRENCY ||
      valueFormat === VALUE_FORMAT_PERCENT ||
      valueFormat === VALUE_FORMAT_PERCENTAGE
    );
  }

  formatCellValue(value, valueFormat) {
    if (value === null || value === undefined || value === '') return '';
    if (!valueFormat) return value;

    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return value;

    if (valueFormat === VALUE_FORMAT_CURRENCY) {
      return this.formatCurrencyValue(numberValue);
    }

    if (valueFormat === VALUE_FORMAT_PERCENT || valueFormat === VALUE_FORMAT_PERCENTAGE) {
      return `${this.formatNumber(numberValue)}%`;
    }

    return value;
  }

  formatNumber(value) {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  getCurrencySymbol() {
    const currency = this.currencyValue;
    if (!currency) return '';

    if (currency.length === 3) {
      try {
        const parts = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          currencyDisplay: 'symbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).formatToParts(0);
        const symbol = parts.find((part) => part.type === 'currency')?.value;
        return symbol || currency;
      } catch (error) {
        return currency;
      }
    }

    return currency;
  }

  formatCurrencyValue(value) {
    const currency = this.currencyValue;

    if (currency && currency.length === 3) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      } catch (error) {
        return `${currency} ${this.formatNumber(value)}`;
      }
    }

    return `${this.getCurrencySymbol()}${this.formatNumber(value)}`;
  }

  buildEditableInput(col, value, entry) {
    const key = col.key;
    const type = col.type || COLUMN_TYPE_NUMBER;
    const isBoolean = type === COLUMN_TYPE_BOOLEAN || entry.row.row_type === ROW_TYPE_BOOLEAN;

    if (isBoolean) {
      return this.buildBooleanSelect(key, value, entry);
    }

    const input = document.createElement('input');
    input.type = type === COLUMN_TYPE_TEXT ? COLUMN_TYPE_TEXT : COLUMN_TYPE_NUMBER;
    input.value = value ?? '';
    if (type !== COLUMN_TYPE_TEXT) input.step = entry.row.user_editable ? 'any' : '1';

    input.addEventListener('input', (e) => {
      const v =
        type === COLUMN_TYPE_TEXT
          ? e.target.value
          : e.target.value === ''
            ? null
            : parseFloat(e.target.value);

      this.currentJson.sections[entry.sectionIndex].groups[entry.groupIndex].rows[
        entry.rowIndex
      ].values[key] = v;

      this.updateJsonOutput();

      // Schedule auto-save on input (debounced) only when formula calculations exist
      if (this.currentFormulaClass) {
        this.scheduleAutoSave();
      }
    });

    // Add arrow key navigation and prevent Enter from submitting form
    input.addEventListener('keydown', (e) => {
      // Move to next cell on Enter
      if (e.key === 'Enter') {
        e.preventDefault();
        this.moveToNextCell(input);
        return;
      }
      this.handleArrowKeyNavigation(e, input);
    });

    // Select all text on focus
    input.addEventListener('focus', (e) => {
      e.target.select();
    });

    return input;
  }

  buildBooleanSelect(key, value, entry) {
    const select = document.createElement('select');
    select.className = 'formula-boolean-select';

    [
      { v: '', t: 'Select...' },
      { v: 'true', t: 'True' },
      { v: 'false', t: 'False' },
    ].forEach((opt) => {
      const o = document.createElement('option');
      o.value = opt.v;
      o.textContent = opt.t;
      select.appendChild(o);
    });

    select.value =
      value === true || value === 'true'
        ? 'true'
        : value === false || value === 'false'
          ? 'false'
          : '';

    select.addEventListener('change', (e) => {
      let newValue = null;
      if (e.target.value === 'true') newValue = true;
      if (e.target.value === 'false') newValue = false;

      this.currentJson.sections[entry.sectionIndex].groups[entry.groupIndex].rows[
        entry.rowIndex
      ].values[key] = newValue;

      this.updateJsonOutput();

      // Schedule auto-save on change only when formula calculations exist
      if (this.currentFormulaClass) {
        this.scheduleAutoSave();
      }
    });

    // Add arrow key navigation and open select on Enter
    select.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (typeof select.showPicker === 'function') {
          select.showPicker();
        } else {
          select.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        }
        return;
      }
      this.handleArrowKeyNavigation(e, select);
    });

    return select;
  }

  /* ======================================================
   * ARROW KEY NAVIGATION
   * ====================================================== */

  handleArrowKeyNavigation(event, currentElement) {
    const key = event.key;

    // Only handle arrow keys
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
      return;
    }

    // For text inputs, only navigate on left/right if cursor is at the edge
    if (currentElement.tagName === 'INPUT' && currentElement.type === 'text') {
      const cursorPos = currentElement.selectionStart;
      const textLength = currentElement.value.length;

      // Don't navigate left if cursor is not at the start
      if (key === 'ArrowLeft' && cursorPos > 0) {
        return;
      }

      // Don't navigate right if cursor is not at the end
      if (key === 'ArrowRight' && cursorPos < textLength) {
        return;
      }
    }

    // Get the current cell (td)
    const currentCell = currentElement.closest('td');
    if (!currentCell) return;

    const currentRow = currentCell.parentElement;
    if (!currentRow) return;

    const table = currentRow.closest('table');
    if (!table) return;

    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    let targetCell = null;

    switch (key) {
      case 'ArrowUp':
        event.preventDefault();
        targetCell = this.findCellAbove(currentCell, currentRow, tbody);
        break;
      case 'ArrowDown':
        event.preventDefault();
        targetCell = this.findCellBelow(currentCell, currentRow, tbody);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        targetCell = this.findCellToLeft(currentCell, currentRow);
        break;
      case 'ArrowRight':
        event.preventDefault();
        targetCell = this.findCellToRight(currentCell, currentRow);
        break;
    }

    if (targetCell) {
      const targetInput = targetCell.querySelector('input, select');
      if (targetInput) {
        targetInput.focus();
        // For input fields, select all text for easy replacement
        if (targetInput.tagName === 'INPUT' && targetInput.type !== 'checkbox') {
          targetInput.select();
        }
      }
    }
  }

  findCellAbove(currentCell, currentRow, tbody) {
    const currentCellIndex = this.getCellIndex(currentCell);
    let previousRow = currentRow.previousElementSibling;

    while (previousRow) {
      const cell = this.getCellAtIndex(previousRow, currentCellIndex);
      if (cell && this.hasEditableInput(cell)) {
        return cell;
      }
      previousRow = previousRow.previousElementSibling;
    }

    return null;
  }

  findCellBelow(currentCell, currentRow, tbody) {
    const currentCellIndex = this.getCellIndex(currentCell);
    let nextRow = currentRow.nextElementSibling;

    while (nextRow) {
      const cell = this.getCellAtIndex(nextRow, currentCellIndex);
      if (cell && this.hasEditableInput(cell)) {
        return cell;
      }
      nextRow = nextRow.nextElementSibling;
    }

    return null;
  }

  findCellToLeft(currentCell, currentRow) {
    let previousCell = currentCell.previousElementSibling;

    while (previousCell) {
      if (this.hasEditableInput(previousCell)) {
        return previousCell;
      }
      previousCell = previousCell.previousElementSibling;
    }

    return null;
  }

  findCellToRight(currentCell, currentRow) {
    let nextCell = currentCell.nextElementSibling;

    while (nextCell) {
      if (this.hasEditableInput(nextCell)) {
        return nextCell;
      }
      nextCell = nextCell.nextElementSibling;
    }

    return null;
  }

  getCellIndex(cell) {
    const row = cell.parentElement;
    const table = row.closest('table');
    const tbody = table.querySelector('tbody');
    const allRows = Array.from(tbody.querySelectorAll('tr'));
    const rowIndex = allRows.indexOf(row);

    return this.getVisualColumnIndex(cell, row, rowIndex, allRows);
  }

  getCellAtIndex(row, visualColumnIndex) {
    const table = row.closest('table');
    const tbody = table.querySelector('tbody');
    const allRows = Array.from(tbody.querySelectorAll('tr'));
    const rowIndex = allRows.indexOf(row);

    return this.getCellAtVisualColumn(row, rowIndex, visualColumnIndex, allRows);
  }

  // Calculate the visual column index accounting for rowspan
  getVisualColumnIndex(cell, row, rowIndex, allRows) {
    const cells = Array.from(row.children);
    const cellIndexInRow = cells.indexOf(cell);

    let visualColumn = 0;

    // Count columns occupied by rowspan cells from previous rows
    for (let prevRowIdx = 0; prevRowIdx < rowIndex; prevRowIdx++) {
      const prevRow = allRows[prevRowIdx];
      const prevCells = Array.from(prevRow.children);

      for (const prevCell of prevCells) {
        const rowspan = parseInt(prevCell.getAttribute('rowspan') || '1');

        // If this cell spans into our current row
        if (prevRowIdx + rowspan > rowIndex) {
          const prevCellVisualColumn = this.getVisualColumnIndex(
            prevCell,
            prevRow,
            prevRowIdx,
            allRows
          );
          // We need to skip this column position
          if (visualColumn === prevCellVisualColumn) {
            visualColumn++;
          }
        }
      }
    }

    // Now add the position within the current row
    for (let i = 0; i < cellIndexInRow; i++) {
      visualColumn++;
    }

    return visualColumn;
  }

  // Get cell at a specific visual column index
  getCellAtVisualColumn(row, rowIndex, visualColumnIndex, allRows) {
    let currentVisualColumn = 0;

    // First, check if any rowspan cells from previous rows occupy this column
    for (let prevRowIdx = 0; prevRowIdx < rowIndex; prevRowIdx++) {
      const prevRow = allRows[prevRowIdx];
      const prevCells = Array.from(prevRow.children);

      for (const prevCell of prevCells) {
        const rowspan = parseInt(prevCell.getAttribute('rowspan') || '1');

        // If this cell spans into our current row
        if (prevRowIdx + rowspan > rowIndex) {
          const prevCellVisualColumn = this.getVisualColumnIndex(
            prevCell,
            prevRow,
            prevRowIdx,
            allRows
          );

          if (prevCellVisualColumn === visualColumnIndex) {
            // The target column is occupied by a rowspan cell
            return prevCell;
          }

          if (prevCellVisualColumn < visualColumnIndex) {
            // Skip this column
            if (currentVisualColumn === prevCellVisualColumn) {
              currentVisualColumn++;
            }
          }
        }
      }
    }

    // Now look for the cell in the current row
    const cells = Array.from(row.children);
    for (const cell of cells) {
      if (currentVisualColumn === visualColumnIndex) {
        return cell;
      }
      currentVisualColumn++;
    }

    return null;
  }

  hasEditableInput(cell) {
    const input = cell.querySelector('input, select');
    return input && !input.disabled && !input.readOnly;
  }

  moveToNextCell(currentElement) {
    // Get the current cell
    const currentCell = currentElement.closest('td');
    if (!currentCell) return;

    const currentRow = currentCell.parentElement;
    if (!currentRow) return;

    const table = currentRow.closest('table');
    if (!table) return;

    const tbody = table.querySelector('tbody');
    if (!tbody) return;

    // Try to find the next cell to the right first
    let targetCell = this.findCellToRight(currentCell, currentRow);

    // If no cell to the right, try the first cell in the next row
    if (!targetCell) {
      let nextRow = currentRow.nextElementSibling;

      while (nextRow) {
        // Find the first editable cell in this row
        const cells = Array.from(nextRow.children);
        targetCell = cells.find((cell) => this.hasEditableInput(cell));

        if (targetCell) break;
        nextRow = nextRow.nextElementSibling;
      }
    }

    // If we found a target cell, focus it
    if (targetCell) {
      const targetInput = targetCell.querySelector('input, select');
      if (targetInput) {
        targetInput.focus();
        // For input fields, select all text for easy replacement
        if (targetInput.tagName === 'INPUT' && targetInput.type !== 'checkbox') {
          targetInput.select();
        }
      }
    }
  }

  updateAddSectionButtonVisibility() {
    if (this.hasAddSectionButtonTarget) {
      const addBlockConfig = this.currentJson?.add_block;
      const hasAddBlock = addBlockConfig && addBlockConfig.enabled;

      if (hasAddBlock) {
        this.addSectionButtonTarget.style.display = 'inline-block';
        // Update button label if provided
        const buttonLabel = addBlockConfig.button_label || 'Add Block';
        this.addSectionButtonTarget.textContent = buttonLabel;
      } else {
        this.addSectionButtonTarget.style.display = 'none';
      }
    }
  }

  isRowDimensionKey(key) {
    // Common row dimension keys that should be treated as hierarchy
    const commonDimensions = [
      'gender',
      'employment_type',
      'contract_status',
      'age',
      'type_of_event',
      'age_group',
      'seniority_level',
      'description',
      'status',
    ];
    return key === this._detectedRowDimensionKey || commonDimensions.includes(key);
  }

  getHierarchyValue(row, key) {
    if (row[key] !== undefined) return row[key];
    if (row.values && row.values[key] !== undefined) return row.values[key];
    return '';
  }

  updateJsonOutput() {
    if (this.currentJson) {
      const jsonString = JSON.stringify(this.currentJson);

      // Update JSON output display if it exists (for standalone editor)
      if (this.hasJsonOutputTarget) {
        this.jsonOutputTarget.textContent = JSON.stringify(this.currentJson, null, 2);
      }

      // Update hidden field for form submission (for modal)
      if (this.hasHiddenFieldTarget) {
        this.hiddenFieldTarget.value = jsonString;
      }
    }
  }

  exportJson() {
    this.updateJsonOutput();
  }

  async copyJson() {
    const text = JSON.stringify(this.currentJson, null, 2);

    try {
      await navigator.clipboard.writeText(text);
      this.showSuccess('JSON copied to clipboard!');
    } catch (error) {
      this.showError('Failed to copy JSON');
    }
  }

  addSection() {
    if (!this.currentJson) {
      this.showError('No formula loaded');
      return;
    }

    const addBlockConfig = this.currentJson?.add_block;

    if (!addBlockConfig || !addBlockConfig.enabled) {
      this.showError('Add block not configured');
      return;
    }

    const sectionIndexes = addBlockConfig.section_indexes || [];
    const insertOffsetFromEnd = addBlockConfig.insert_offset_from_end || 0;
    const blockLabel = addBlockConfig.block_label || 'Block';

    if (sectionIndexes.length === 0) {
      this.showError('No sections configured to copy');
      return;
    }

    // Get the template sections (sections to copy)
    const templateSections = sectionIndexes
      .map((idx) => this.currentJson.sections[idx])
      .filter(Boolean);

    if (templateSections.length === 0) {
      this.showError('Template sections not found');
      return;
    }

    // Calculate insertion index (from the end)
    const insertIndex = this.currentJson.sections.length - insertOffsetFromEnd;

    // Count existing extra sections to generate unique names
    const extraSectionsCount = this.currentJson.sections.filter((s) => s.is_extra).length;
    const blockNumber = Math.floor(extraSectionsCount / sectionIndexes.length) + 1;

    // Create new sections based on templates
    const newSections = templateSections.map((templateSection, templateIdx) => {
      // Generate section name based on whether it's a section total or regular section
      let sectionName;
      if (templateSection.section_total_label) {
        // This template is a section with totals - use pattern like "Region - 1"
        sectionName = `${blockLabel} - ${blockNumber}`;
      } else {
        // Check if this looks like a total section (has section_total row_type)
        const isTotal = templateSection.groups?.some((g) =>
          g.rows?.some((r) => r.row_type === ROW_TYPE_SECTION_TOTAL)
        );

        if (isTotal) {
          sectionName = `Total ${blockLabel} - ${blockNumber}`;
        } else {
          sectionName = `${blockLabel} - ${blockNumber}`;
        }
      }

      // Create a new section based on the template
      const newSection = {
        name: sectionName,
        editable: true,
        is_extra: true,
        groups: templateSection.groups.map((group) => ({
          title: group.title,
          type: group.type,
          subtotal_placement: group.subtotal_placement,
          subtotal_level: group.subtotal_level,
          rows: group.rows.map((row) => {
            const newValues = Object.keys(row.values || {}).reduce((acc, key) => {
              // Keep hierarchy values, reset numeric values to null
              if (this._levelColumnKeys && key === this._levelColumnKeys[0]) {
                acc[key] = sectionName;
              } else if (typeof row.values[key] === 'string') {
                acc[key] = row.values[key]; // Keep string values (like dimension labels)
              } else {
                acc[key] = row.row_type === ROW_TYPE_NORMAL ? null : 0.0; // Reset numeric values
              }
              return acc;
            }, {});

            // Also ensure top-level level key exists on the row object (some rows use row.level_1)
            const newRow = {
              ...row,
              values: newValues,
            };

            if (this._levelColumnKeys && newRow[this._levelColumnKeys[0]] !== undefined) {
              newRow[this._levelColumnKeys[0]] = sectionName;
            }

            return newRow;
          }),
        })),
      };

      // Copy section_total_label if it exists in template
      if (templateSection.section_total_label) {
        newSection.section_total_label = `Total ${blockLabel} - ${blockNumber}`;
      }

      return newSection;
    });

    // If we have section totals enabled, add them after the new sections
    const sectionsToInsert = [];
    newSections.forEach((newSection, idx) => {
      sectionsToInsert.push(newSection);

      // Add section total if it exists in template
      if (newSection.section_total_label) {
        const sectionTotalSection = {
          name: newSection.section_total_label,
          editable: false,
          is_extra: true,
          groups: [
            {
              title: '',
              rows: [
                {
                  row_type: ROW_TYPE_SECTION_TOTAL,
                  values: Object.keys(newSection.groups[0].rows[0].values || {}).reduce(
                    (acc, key) => {
                      acc[key] = 0.0;
                      return acc;
                    },
                    {}
                  ),
                  cell_styles: newSection.groups[0].rows[0].cell_styles,
                },
              ],
            },
          ],
        };

        // Add level column values for section total
        if (this._levelColumnKeys && this._levelColumnKeys[0]) {
          sectionTotalSection.groups[0].rows[0][this._levelColumnKeys[0]] =
            newSection.section_total_label;
          sectionTotalSection.groups[0].rows[0].values[this._levelColumnKeys[0]] = null;
        }

        sectionsToInsert.push(sectionTotalSection);
      }
    });

    // Insert the new sections at the calculated position
    this.currentJson.sections.splice(insertIndex, 0, ...sectionsToInsert);

    // Re-render the table
    this.renderTable();
    this.updateJsonOutput();

    const message =
      sectionsToInsert.length === 1
        ? `Section "${sectionsToInsert[0].name}" added!`
        : `Block "${blockLabel} - ${blockNumber}" (${sectionsToInsert.length} sections) added!`;

    this.showSuccess(message);
  }

  removeSection(sectionIndex) {
    if (!this.currentJson) {
      this.showError('No formula loaded');
      return;
    }

    const section = this.currentJson.sections[sectionIndex];

    // Only allow removing extra sections
    if (!section.is_extra) {
      this.showError('Cannot remove default sections');
      return;
    }

    // Confirm removal
    if (!confirm(`Are you sure you want to remove section "${section.name}"?`)) {
      return;
    }

    // Remove the section
    this.currentJson.sections.splice(sectionIndex, 1);

    // Re-render the table
    this.renderTable();
    this.updateJsonOutput();

    this.showSuccess(`Section "${section.name}" removed successfully.`);
  }

  showLoading(message = 'Loading...') {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = 'block';
    }
    if (this.hasLoadingTextTarget) {
      this.loadingTextTarget.textContent = message;
    }
    if (this.hasLoadButtonTarget) {
      this.loadButtonTarget.disabled = true;
    }
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = true;
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.style.display = 'none';
    }
    if (this.hasLoadButtonTarget) {
      this.loadButtonTarget.disabled = false;
    }
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.disabled = false;
    }
  }

  showError(message) {
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message;
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.style.display = 'block';
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.style.display = 'none';
    }
  }

  showSuccess(message) {
    // Create a temporary success message
    const successDiv = document.createElement('div');
    successDiv.style.cssText =
      'background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 0.5rem; padding: 1rem; margin-bottom: 1.5rem;';
    successDiv.innerHTML = `<p style="color: #15803d;">${message}</p>`;

    this.errorMessageTarget.parentNode.insertBefore(successDiv, this.errorMessageTarget);

    setTimeout(() => {
      successDiv.remove();
    }, 3000);
  }

  showEditor() {
    this.editorContainerTarget.style.display = 'block';
  }

  getCsrfToken() {
    return document.querySelector('[name="csrf-token"]')?.content || '';
  }
}
